import React from "react";
import { Action } from "./actions";

// Teleprompter-like scroller: scrolls the chapter text at a readable speed.
// Gesture integration: pass gestureAction prop as one of 'prev' or 'next'
// to jump the playhead by -5/+5 words. You can also pass 'pause'/'play'.
//
// Props:
// - text: string (required)
// - wpm?: number (default 180) — approximate reading speed
// - gestureAction?: 'prev' | 'next' | 'pause' | 'play' | null
// - className?: string — extra class for root
// - style?: object — inline style for root
// - onEnd?: () => void — fires when scroll reaches the end

export default class ScriptScroller extends React.Component {
  static defaultProps = {
    wpm: 180,
    gestureAction: null,
    sentencePauseMs: 800, // pause after sentence-ending punctuation
    commaPauseMs: 400, // pause after commas and colons
  };

  constructor(props) {
    super(props);
    this.containerRef = React.createRef();
    this.contentRef = React.createRef();
    this.state = {
      durationSec: 0,
      totalScrollable: 0,
      startedAt: 0,
      // Start paused by default; requires a 'play' gesture to begin scrolling
      paused: true,
      baseOffsetSec: 0,
      tokens: [],            // text split into tokens (words and whitespace)
      wordCount: 0,          // number of word tokens
      currentWordIndex: 0,   // which word is currently highlighted
      cumTimes: [],          // cumulative times at the end of each word (sec), includes sentence pauses
    };
    this._raf = 0;
    this._lastGestureKey = null;
  }

  componentDidMount() {
    this.recomputeMetrics();
    window.addEventListener('resize', this.recomputeMetrics);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.recomputeMetrics);
    if (this._raf) cancelAnimationFrame(this._raf);
  }

  componentDidUpdate(prevProps) {
    // React to gesture action transitions only (edge-triggered)
    if (this.props.gestureAction && this.props.gestureAction !== prevProps.gestureAction) {
      this.applyGesture(this.props.gestureAction);
    }
    // If text changed or wpm changed, recompute
    if (this.props.text !== prevProps.text || this.props.wpm !== prevProps.wpm) {
      this.recomputeMetrics();
    }
  }

  getElapsedSec() {
    const { startedAt, baseOffsetSec, paused } = this.state;
    const now = performance.now();
    if (paused) return baseOffsetSec;
    return Math.max(0, (now - startedAt) / 1000) + baseOffsetSec;
  }

  recomputeMetrics = () => {
    // Tokenize the text and compute duration from word count & wpm
    const text = String(this.props.text || '');
    const tokens = this.tokenize(text);
    const wordIndices = [];
    for (let i = 0; i < tokens.length; i++) if (!/\s+/.test(tokens[i])) wordIndices.push(i);
    const wordCount = wordIndices.length;
    const wps = (this.props.wpm || 180) / 60;
    const perWord = wps > 0 ? 1 / wps : 0.5; // fallback
    const pauseSec = (Number(this.props.sentencePauseMs) || 0) / 1000;
    const commaPauseSec = (Number(this.props.commaPauseMs) || 0) / 1000;

    // Build cumulative times including pauses after punctuation
    const cumTimes = new Array(Math.max(0, wordCount));
    let t = 0;
    for (let wi = 0; wi < wordCount; wi++) {
      t += perWord;
      // Check punctuation at end of this word token
      const tok = tokens[wordIndices[wi]] || "";
      const endsSentence = /[.!?…]+["')\]]*$/.test(tok);
      const endsWithCommaOrColon = /[,;:]+["')\]]*$/.test(tok);
      
      if (endsSentence) {
        t += pauseSec;
      } else if (endsWithCommaOrColon) {
        t += commaPauseSec;
      }
      cumTimes[wi] = t;
    }
    const durationSec = cumTimes.length ? cumTimes[cumTimes.length - 1] : 1;

    // Preserve current elapsed time across WPM or text changes
    const prevElapsed = this.getElapsedSec();
    const clampedElapsed = Math.min(durationSec, Math.max(0, prevElapsed));
    const currentWordIndex = this.indexForElapsed(cumTimes, clampedElapsed);

    // Ensure layout is updated before measuring
    this._measureAndStart(durationSec, tokens, wordCount, clampedElapsed, currentWordIndex, cumTimes);
  };

  _measureAndStart(durationSec, tokens, wordCount, baseElapsed = 0, currentWordIndex = 0, cumTimes = []) {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => {
      const container = this.containerRef.current;
      const content = this.contentRef.current;
      if (!container || !content) return;
      const totalScrollable = Math.max(0, content.scrollHeight - container.clientHeight);
      this.setState({
        durationSec,
        totalScrollable,
        startedAt: performance.now(),
        // Preserve current pause state so we don't auto-start on mount/recompute
        paused: this.state.paused,
        baseOffsetSec: baseElapsed,
        tokens,
        wordCount,
        currentWordIndex,
        cumTimes,
      }, () => {
        this.ensureCurrentVisible();
        this.tick();
      });
    });
  }

  // Binary search: first index where cumTimes[i] > elapsed
  indexForElapsed(cumTimes, elapsed) {
    if (!Array.isArray(cumTimes) || cumTimes.length === 0) return 0;
    let lo = 0, hi = cumTimes.length - 1, ans = cumTimes.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (cumTimes[mid] > elapsed) {
        ans = mid;
        hi = mid - 1;
      } else {
        lo = mid + 1;
      }
    }
    return Math.max(0, Math.min(ans, cumTimes.length - 1));
  }

  tick = () => {
    const container = this.containerRef.current;
    if (!container) return;

    const { paused, baseOffsetSec, durationSec, totalScrollable, startedAt, wordCount, cumTimes } = this.state;
    if (!paused) {
      const now = performance.now();
      const elapsedSec = Math.max(0, (now - startedAt) / 1000) + baseOffsetSec;
      const t = Math.min(elapsedSec, durationSec);
      // Update current word based on elapsed time using cumulative timeline (includes sentence pauses)
      const idx = this.indexForElapsed(cumTimes, elapsedSec);
      if (idx !== this.state.currentWordIndex) {
        // lightweight state update when index changes (~WPS times per second)
        this.setState({ currentWordIndex: idx }, () => this.ensureCurrentVisible());
      } else {
        this.ensureCurrentVisible();
      }
      if (t >= durationSec) {
        // reached end
        if (typeof this.props.onEnd === 'function') this.props.onEnd();
        return; // stop ticking
      }
    }
    this._raf = requestAnimationFrame(this.tick);
  };

  ensureCurrentVisible() {
    try {
      const container = this.containerRef.current;
      const content = this.contentRef.current;
      if (!container || !content) return;
      const el = content.querySelector('.lrp-word.is-current');
      if (!el) return;
      const elTop = el.offsetTop; // relative to content
      const elBottom = elTop + el.offsetHeight;
      const viewTop = container.scrollTop;
      const viewBottom = viewTop + container.clientHeight;
      const styles = window.getComputedStyle(container);
      const padTop = parseFloat(styles.paddingTop || '0') || 0;
      const padBottom = parseFloat(styles.paddingBottom || '0') || 0;
      
      // Dynamic buffer based on element height (font size)
      // Use 50% of element height as buffer to ensure word is always well-centered
      const dynamicBuffer = Math.max(20, el.offsetHeight * 0.5);
      const topBuffer = padTop + dynamicBuffer;
      const bottomBuffer = padBottom + dynamicBuffer;

      // Guards that respect container padding so the current word isn't clipped at the very top/bottom
      const upperGuard = viewTop + topBuffer;
      const lowerGuard = viewBottom - bottomBuffer;

      if (elTop < upperGuard) {
        // Scroll so the word sits just below the top padding + buffer
        container.scrollTop = Math.max(0, elTop - topBuffer);
      } else if (elBottom > lowerGuard) {
        // Scroll up so the word's bottom is above the bottom padding + buffer
        const desired = elBottom - (container.clientHeight - bottomBuffer);
        container.scrollTop = Math.min(
          content.scrollHeight - container.clientHeight,
          Math.max(0, desired)
        );
      }
    } catch (_) {}
  }

  tokenize(text) {
    // Split text into words and whitespace tokens, preserving separators
    // Example: "Hello, world!" => ["Hello,", " ", "world!"]
    // We prefer a simple whitespace splitter that keeps whitespace tokens
    const parts = String(text).split(/(\s+)/);
    // Ensure no empty tokens except meaningful whitespace
    return parts.filter(t => t.length > 0);
  }

  // External gesture mapping
  applyGesture(action) {
    if (!action) return;
    if (action === Action.PAUSE) {
      this.pause();
      return;
    }
    if (action === Action.STOP) {
      this.stop();
      return;
    }
    if (action === Action.PLAY || action === Action.START) {
      this.play();
      return;
    }
    if (action === Action.PREV) {
      this.seekWords(-5);
      return;
    }
    if (action === Action.NEXT) {
      this.seekWords(+5);
      return;
    }
  }

  pause() {
    if (this.state.paused) return;
    const now = performance.now();
    const elapsed = (now - this.state.startedAt) / 1000;
    this.setState({ paused: true, baseOffsetSec: this.state.baseOffsetSec + elapsed });
  }

  play() {
    if (!this.state.paused) return;
    this.setState({ paused: false, startedAt: performance.now() }, () => {
      this.ensureCurrentVisible();
      this.tick();
    });
  }

  stop() {
    // Reset to top and pause
    const container = this.containerRef.current;
    if (container) container.scrollTop = 0;
    this.setState({ paused: true, baseOffsetSec: 0, startedAt: performance.now(), currentWordIndex: 0 }, () => {
      this.ensureCurrentVisible();
    });
  }

  seekBy(seconds) {
    const { durationSec, startedAt, baseOffsetSec, paused } = this.state;
    const now = performance.now();
    const elapsed = (now - startedAt) / 1000;
    const current = paused ? baseOffsetSec : baseOffsetSec + elapsed;
    const next = Math.min(durationSec, Math.max(0, current + seconds));
    this.setState({ baseOffsetSec: next, startedAt: performance.now() });
  }

  // New: seek by word count while maintaining paused/playing state
  seekWords(deltaWords) {
    const { cumTimes, currentWordIndex, durationSec, paused } = this.state;
    if (!Array.isArray(cumTimes) || cumTimes.length === 0) return;
    const targetIndex = Math.max(0, Math.min(cumTimes.length - 1, currentWordIndex + (deltaWords || 0)));
    const targetSec = targetIndex > 0 ? cumTimes[targetIndex - 1] : 0;
    // Keep the same paused state; reset startedAt so play continues smoothly
    this.setState({
      baseOffsetSec: Math.min(durationSec, Math.max(0, targetSec)),
      startedAt: performance.now(),
      currentWordIndex: targetIndex,
    }, () => this.ensureCurrentVisible());
  }

  render() {
    const { className, style } = this.props;
    const { tokens, currentWordIndex } = this.state;
    return (
      <div
        className={`lrp-scroller ${className || ''}`}
        style={style}
        ref={this.containerRef}
      >
        <div className="lrp-scroller-content" ref={this.contentRef}>
          {tokens.length > 0
            ? tokens.map((tok, i) => {
                if (/\s+/.test(tok)) {
                  return <span key={`ws-${i}`}>{tok}</span>;
                }
                const wordIdx = (() => {                  
                  let count = 0;
                  for (let k = 0; k <= i; k++) if (!/\s+/.test(tokens[k])) count++;
                  return count - 1;
                })();
                const isCurrent = wordIdx === currentWordIndex;
                const isRead = wordIdx < currentWordIndex;
                return (
                  <span
                    key={`w-${i}`}
                    className={`lrp-word${isCurrent ? ' is-current' : ''}${isRead ? ' is-read' : ''}`}
                  >
                    {tok}
                  </span>
                );
              })
            : null}
        </div>
      </div>
    );
  }
}
